import React from 'react';
import {ReactNode, useCallback, useMemo, useState} from 'react';
import {FilterCacheKeySlice, FilterConfigSlice, FilterSelectionSlice, FiltersContext} from './FiltersContext';
import isEqual from 'lodash/fp/isEqual';
import {FilterConfigSections, FilterSelectionSections, FilterSliceKeyType} from '@lib/utils';

type FiltersProviderProps = {
  children: ReactNode;
};

export const FiltersProvider = ({children}: FiltersProviderProps) => {
  const [selectionSlices, setSelectionSlices] = useState<FilterSelectionSlice>({});
  const [configSlices, setConfigSlices] = useState<FilterConfigSlice>({});
  const [cacheKeySlices, setCacheKeySlices] = useState<FilterCacheKeySlice>({});

  const getInitFilterSelection = useCallback(
    (config: FilterConfigSections) =>
      Object.entries(config).reduce((acc, [key, value]) => {
        const getValuesToResetTo = (): string[] => {
          if (!value) return [];

          const {type, defaultFilterOptions} = value;
          switch (type) {
            case 'and':
            case 'or':
              return defaultFilterOptions;
            case 'singleMandatory':
              return [defaultFilterOptions];
            case 'singleOptional':
            default:
              return [];
          }
        };

        return {
          ...acc,
          [key]: getValuesToResetTo(),
        };
      }, {} as FilterSelectionSections),
    [],
  );

  const setSelectionSlice = useCallback((sliceId: FilterSliceKeyType, newSlice: FilterSelectionSections) => {
    setSelectionSlices((prev) => {
      const current = prev[sliceId];
      if (isEqual(current, newSlice)) return prev;

      return {...prev, [sliceId]: newSlice};
    });
  }, []);

  const setConfigSlice = useCallback(
    (sliceId: FilterSliceKeyType, newSlice: FilterConfigSections) => {
      setConfigSlices((prev) => {
        const current = prev[sliceId];
        if (isEqual(current, newSlice)) return prev;

        setSelectionSlice(sliceId, getInitFilterSelection(newSlice));

        return {...prev, [sliceId]: newSlice};
      });
    },
    [getInitFilterSelection, setSelectionSlice],
  );

  const setCacheKeySlice = useCallback(
    (sliceId: FilterSliceKeyType, newCacheKey: string, resetSelection: FilterSelectionSections) => {
      setCacheKeySlices((prev) => {
        const current = prev[sliceId];
        if (current === newCacheKey) return prev;

        setSelectionSlice(sliceId, resetSelection);

        return {...prev, [sliceId]: newCacheKey};
      });
    },
    [setSelectionSlice],
  );

  const value = useMemo(() => {
    return {
      setSelectionSlice,
      selectionSlices,
      configSlices,
      setConfigSlice,
      getInitFilterSelection,
      cacheKeySlices,
      setCacheKeySlice,
    };
  }, [
    setSelectionSlice,
    selectionSlices,
    configSlices,
    setConfigSlice,
    getInitFilterSelection,
    cacheKeySlices,
    setCacheKeySlice,
  ]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
